// custom-workshop-calendar.component.ts
import { Component, Input, Output, EventEmitter, Renderer2, OnDestroy, ViewChild } from '@angular/core';
import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services';

@Component({
    selector: 'app-custom-workshop-calendar',
    templateUrl: './custom-workshop-calendar.component.html',
    styleUrls: ['./custom-workshop-calendar.component.scss'],
    standalone: false
})
export class CustomWorkshopCalendarComponent implements OnDestroy {
  @Input() defaultBranch: string;
  @Input() currentMonth: number;
  @Input() selectedFitDays: string[] = [];
  @Input() class: string;

  @Output() dateSelected = new EventEmitter<NgbDateStruct>();
  @Output() monthNavigated = new EventEmitter<any>();
  @Output() calendarOpened = new EventEmitter<any>();
  @ViewChild('workshopDatePicker') workshopDatePicker: NgbInputDatepicker;

  dayDetails: any[] = [];
  fitDays: string;
  fitHours: string;
  isLoadingCalendarData = false;
  private unSubscribe$ = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    private eventService: ApiService // inject your service
  ) {}

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  streamOpened(event) {
    const workshopBookingTF = true;
    if (workshopBookingTF) {
      this.customizeCalendarStyles();
      this.fetchDataForVisibleDates();
    } else {
      this.applyDefaultStyles();
    }
  }

  navigateCalendar(event) {
    this.currentMonth = this.currentMonth;
    this.monthNavigated.emit(event);

    setTimeout(() => {
      this.fetchDataForVisibleDates();
    }, 100);
  }

  goToMonth(): NgbDateStruct {
    const today = new Date();
    const month = this.currentMonth ? this.currentMonth - 1 : today.getMonth();
    const year = today.getFullYear();
    return { day: 1, month: month + 1, year };
  }

  onDateSelect(date: NgbDateStruct) {
    this.dateSelected.emit(date);
    this.workshopDatePicker.close();
  }

  private customizeCalendarStyles() {
    // Month container styling
    document.querySelectorAll('.ngb-dp-month').forEach((el) => {
      (el as HTMLElement).style.height = '360px';
      (el as HTMLElement).style.width = '420px';
    });

    // Weekdays styling
    document.querySelectorAll('.ngb-dp-weekdays').forEach((el) => {
      el.className = 'ngb-dp-week ngb-dp-weekdays ng-star-inserted ngb-dp-weekdays-custom';
      (el as HTMLElement).style.alignItems = 'center';
      (el as HTMLElement).style.height = '54px';
    });

    // Week styling
    document.querySelectorAll('.ngb-dp-week').forEach((el, i) => {
      if (i === 0) {
        el.className = 'ngb-dp-week ng-star-inserted ngb-dp-week-label-custom';
        (el as HTMLElement).style.justifyContent = 'space-evenly';
      } else {
        el.className = 'ngb-dp-week ng-star-inserted ngb-dp-week-custom';
        (el as HTMLElement).style.justifyContent = 'space-evenly';
        (el as HTMLElement).style.height = '54px';
      }
    });

    // Day styling
    document.querySelectorAll('.ngb-dp-day.ng-star-inserted').forEach((el) => {
      el.classList.add('ngb-dp-day-custom');
      (el as HTMLElement).style.background = 'none';
      (el as HTMLElement).style.backgroundColor = 'transparent';
    });
  }

  private applyDefaultStyles() {
    document.querySelectorAll('.ngb-dp-weekdays').forEach((el) => {
      el.classList.add('ngb-dp-weekdays-defaultMatch');
    });
    document.querySelectorAll('.ngb-dp-week').forEach((el) => {
      el.classList.add('ngb-dp-weekdays-defaultMatch');
    });
    document.querySelectorAll('.ngb-dp-day').forEach((el) => {
      el.classList.add('ngb-dp-weekdays-defaultMatch');
    });
  }

  private fetchDataForVisibleDates() {
    const dayElements = document.querySelectorAll('.ngb-dp-day');
    if (dayElements.length > 0) {
      const firstDate = this.dateToString(dayElements[0]?.getAttribute('aria-label'));
      const lastDate = this.dateToString(dayElements[dayElements.length - 1]?.getAttribute('aria-label'));

      if (firstDate && lastDate) {
        this.fetchMonthlyViewForOrder(firstDate, lastDate);
      }
    }
  }

  fetchMonthlyViewForOrder(startDate: string, endDate: string) {
    this.removeExistingHeaders();
    this.showLoadingOverlay();

    this.isLoadingCalendarData = true;
    this.eventService
      .fetchMonthlyViewForOrder({
        startDate,
        endDate,
        orderNumber: '',
        branch: this.defaultBranch,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res: any) => {
        this.isLoadingCalendarData = false;
        this.removeLoadingOverlay();

        if (res) {
          this.dayDetails = res?.dates;
          this.fitDays = res?.fitDays;
          this.fitHours = res?.fitHours;
          setTimeout(() => {
            this.displayMonth(res?.dates, res?.fitDays, res?.fitHours);
          }, 100);
        }
      });
  }

  private removeExistingHeaders() {
    document.querySelectorAll('.ngb-dp-week-label-custom-header').forEach((element) => {
      element.remove();
    });
  }

  private showLoadingOverlay() {
    document.querySelectorAll('.pos-calendar-overlay').forEach((el) => el.remove());

    const elements = document.querySelectorAll('.ngb-dp-header');
    elements.forEach((element) => {
      const monthEl = ((element as HTMLElement).closest('.ngb-dp-month') as HTMLElement) || (element.parentElement as HTMLElement);
      if (!monthEl) return;

      this.renderer.setStyle(monthEl, 'position', 'relative');
      this.renderer.setStyle(monthEl, 'width', '400px');  
      const overlay = this.createLoadingOverlay();
      this.renderer.insertBefore(monthEl, overlay, monthEl.firstChild);
    });

    this.addSpinnerKeyframes();
  }

  private createLoadingOverlay() {
    const overlay = this.renderer.createElement('div');
    this.renderer.addClass(overlay, 'pos-calendar-overlay');
    this.renderer.setStyle(overlay, 'position', 'absolute');
    this.renderer.setStyle(overlay, 'top', '0');
    this.renderer.setStyle(overlay, 'left', '0');
    this.renderer.setStyle(overlay, 'right', '0');
    this.renderer.setStyle(overlay, 'bottom', '0');
    this.renderer.setStyle(overlay, 'background', 'rgba(255,255,255,0.85)');
    this.renderer.setStyle(overlay, 'z-index', '9999');
    this.renderer.setStyle(overlay, 'display', 'flex');
    this.renderer.setStyle(overlay, 'align-items', 'center');
    this.renderer.setStyle(overlay, 'justify-content', 'center');
    this.renderer.setStyle(overlay, 'pointer-events', 'none');

    const box = this.renderer.createElement('div');
    this.renderer.setStyle(box, 'text-align', 'center');
    this.renderer.setStyle(box, 'font-weight', '600');
    this.renderer.setStyle(box, 'color', '#333');

    const spinner = this.createSpinner();
    const text = this.renderer.createText('Workshop availability loading...');

    this.renderer.appendChild(box, spinner);
    this.renderer.appendChild(box, text);
    this.renderer.appendChild(overlay, box);

    return overlay;
  }

  private createSpinner() {
    const spinner = this.renderer.createElement('div');
    this.renderer.setStyle(spinner, 'width', '28px');
    this.renderer.setStyle(spinner, 'height', '28px');
    this.renderer.setStyle(spinner, 'border-radius', '50%');
    this.renderer.setStyle(spinner, 'border', '4px solid rgba(0,0,0,0.1)');
    this.renderer.setStyle(spinner, 'border-top-color', '#007bff');
    this.renderer.setStyle(spinner, 'margin', '0 auto 8px');
    this.renderer.setStyle(spinner, 'animation', 'posOverlaySpin 1s linear infinite');
    return spinner;
  }

  private addSpinnerKeyframes() {
    if (!document.getElementById('pos-overlay-spinner-style')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'pos-overlay-spinner-style';
      styleEl.innerHTML = `
        @keyframes posOverlaySpin { 
          from { transform: rotate(0deg); } 
          to { transform: rotate(360deg); } 
        }
      `;
      document.head.appendChild(styleEl);
    }
  }

  private removeLoadingOverlay() {
    document.querySelectorAll('.pos-calendar-overlay').forEach((el) => el.remove());
    const spinnerStyle = document.getElementById('pos-overlay-spinner-style');
    if (spinnerStyle) {
      spinnerStyle.remove();
    }
  }

  displayMonth(dates: any[], fitDays: string, fitHours: string) {
    this.removeExistingHeaders();

    const dayElements = document?.querySelectorAll('.ngb-dp-day');
    dayElements?.forEach((dayElement) => {
      if (dayElement.getAttribute('aria-label')) {
        const dateSearch = this.dateToString(dayElement.getAttribute('aria-label'));

        if (Number(dateSearch.split('/')[1]) === this.currentMonth) {
          this.applyDayStyles(dayElement, dateSearch, dates);
        }
      }
    });
  }

  private applyDayStyles(dayElement: Element, dateSearch: string, dates: any[]) {
    const data = dates.find((f) => f.date === dateSearch);

    if (data && Number(dateSearch.split('/')[1]) === this.currentMonth) {
      this.applyAvailabilityStyles(dayElement, data);
      this.updateHoursDisplay(dayElement, data);
      this.setTooltip(dayElement, data);
    } else {
      this.applyDisabledStyles(dayElement);
    }

    // Check if this date is selected
    const selectedDaysData = this.selectedFitDays.find((f) => f === dateSearch);
    if (selectedDaysData) {
      const childElement = dayElement.children[0]?.children[0] as HTMLElement;
      if (childElement?.id) {
        document.getElementById(childElement.id).className = 'selectedFittingDateCss';
      }
    }
  }

  private applyAvailabilityStyles(dayElement: Element, data: any) {
    const childElement = dayElement.children[0]?.children[0] as HTMLElement;
    if (!childElement?.id) return;

    if (data?.ATPErrorTF) {
      document.getElementById(childElement.id).className = 'atpErrorTFCss';
    } else if (data?.fullyBookedTF) {
      document.getElementById(childElement.id).className = 'fullyBookedTFCss';
    } else if (data?.partialSpaceTF) {
      document.getElementById(childElement.id).className = 'partialSpaceTFCss';
    } else if (data?.spaceAvailable) {
      document.getElementById(childElement.id).className = 'spaceAvailableCss';
    }
  }

  private updateHoursDisplay(dayElement: Element, data: any) {
    const hoursElement = dayElement.children[0]?.children[1] as HTMLElement;
    if (hoursElement?.id) {
      const availableHours = (data.totalHoursAvail - data?.bookedHours).toFixed(2);
      document.getElementById(hoursElement.id).innerHTML = `${availableHours} hrs`;
    }
  }

  private setTooltip(dayElement: Element, data: any) {
    const childElement = dayElement.children[0]?.children[0] as HTMLElement;
    if (childElement?.id) {
      document.getElementById(childElement.id).setAttribute('title', `Booked Hrs: ${data?.bookedHours}\nBooked: ${data?.bookedPerc}%`);
    }
  }

  private applyDisabledStyles(dayElement: Element) {
    const hoursElement = dayElement.children[0]?.children[1] as HTMLElement;
    const childElement = dayElement.children[0]?.children[0] as HTMLElement;

    if (hoursElement?.id) {
      document.getElementById(hoursElement.id).innerHTML = '';
    }

    dayElement.classList.add('customCss');
   

    if (childElement?.id) {
      document.getElementById(childElement.id).className = 'disabled';
    }
  }

  isDisabled = (date: NgbDateStruct, current: { month: number; year: number }) => {
    return current.month !== date.month;
  };

  dateToString(date: any): string {
    if (!date) return '';

    let d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [day, month, year].join('/');
  }
}

